import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import * as CourseService from "../../../../services/CourseService";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PATHS from "../../../../constants/paths";
import {
  COUNTRIES,
  ROLE_ADMIN,
  ROLE_SPEAKER,
} from "../../../../constants/index";
import {
  ButtonAction,
  ButtonClose,
  Icon,
  Option,
  ListOptions,
  DocLink,
  IconDocLink,
} from "./courseList.styled";
import { Can, AbilityContext } from "../../../security/permissions/Can";
import {} from "../../../security/permissions/rolesPermission";
import {
  checkBootstrapSizeBreak,
  SIZES,
} from "../../../../utils/device/checkDevice";
import {
  ShowAlertDialog,
  PaginateList,
} from "@de-prado-dev/react-component-library";
import { getRoleSelector } from "../../../../store/selectors/authSelector";
import { useSelector } from "react-redux";
import { USER_PERMISSIONS } from "../../../security/permissions/rolesPermission";
import PdfViewer from "../../../utils/PdfViewer";
import { Tooltip as ReactTooltip } from "react-tooltip";

const CourseListContainer = ({ filters, sortBy }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const deviceSize = checkBootstrapSizeBreak();
  const [forceRefresh, setForceRefresh] = useState(false);
  const context = useContext(AbilityContext);
  const role = useSelector(getRoleSelector);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [currentPdfTitle, setCurrentPdfTitle] = useState("");

  const columns = [
    {
      header: t("forms.form.table.heads.name"),
      accessorKey: "name",
      cell: (info) => info.getValue(),
    },
    {
      header: t("forms.form.table.heads.description"),
      accessorKey: "description",
      cell: (info) => {
        const descripcion = info.getValue();
        const descriptionShort =
          descripcion.length > 50
            ? descripcion.substring(0, 50) + "..."
            : descripcion;
        return (
          <>
            <span
              data-tooltip-id={info.row?.original?.uuid}
              data-tooltip-content={descripcion}
              data-tooltip-delay-hide={1000}
              style={{ cursor: "pointer", color: "#000000" }}
            >
              {descriptionShort}{" "}
              {descripcion.length > 50 && (
                <i
                  className="fa-solid fa-circle-info"
                  style={{ color: "#f9ae17" }}
                ></i>
              )}
            </span>
            <ReactTooltip
              events={["click"]}
              effect="solid"
              id={info.row?.original?.uuid}
              style={{ maxWidth: "400px" }}
            />
          </>
        );
      },
    },
    {
      header: t("forms.form.table.heads.paises"),
      accessorKey: "countries",
      cell: (info) => {
        const countries = info.getValue();
        return countries
          .map((code) => {
            const result = COUNTRIES.find((c) => c.code === code);
            return result ? t(result.label) : code;
          })
          .join(", ");
      },
    },
    {
      header: t("forms.form.table.heads.documents"),
      accessorKey: " ",
      cell: (info) => renderDocs(info.row.original),
    },
    {
      Header: t(""),
      accessorKey: " ",
      size: 50,
      cell: (info) => renderAction(info.row.original),
    },
  ];

  const goToEdit = (uuid) => {
    navigate(`/adm/course/form/update/${uuid}`);
  };

  const handlePreviewPdf = (url, title) => {
    setCurrentPdfUrl(url);
    setCurrentPdfTitle(title);
    setShowPdfViewer(true);
  };

  const handleClosePdfViewer = () => {
    setShowPdfViewer(false);
    setCurrentPdfUrl(null);
    setCurrentPdfTitle("");
  };

  const renderDocs = (data) => {
    let docLabel = data.name;
    const docUrl = data.documents[0]?.url;

    return (
      <div className="row mt-2">
        <div className="col-10 d-flex align-items-center gap-3">
          <ButtonAction
            onClick={() => handlePreviewPdf(docUrl, docLabel)}
            type="button"
            title="Vista previa"
          >
            <i className="fa-solid fa-eye"></i>
          </ButtonAction>
          <DocLink href={docUrl} target="_blank" rel="noopener noreferrer">
            <IconDocLink className="fa-solid fa-file-pdf"></IconDocLink>{" "}
            {`${docLabel} `}
            {/* ${data.documents[0]?.name} */}
          </DocLink>
          {/* <Can do={USER_PERMISSIONS.DELETE_DOCUMENT}>
            <div className="col-2 d-flex text-end">
              <button
                type="button"
                className="btn p-0 border-0 bg-transparent text-danger"
                onClick={() => onDeleteDoc(doc.uuid)}
              >
                <i className="fa-solid fa-trash-can"></i>
              </button>
            </div>
          </Can> */}
        </div>
      </div>
    );
  };
  const renderAction = (data) => {
    return (
      <div className="text-center">
        <Can do={USER_PERMISSIONS.EDIT_COURSE}>
          <ButtonAction
            onClick={() => goToEdit(data.uuid)}
            type="button"
            title={t("forms.course.buttons.edit")}
          >
            <i className="fa-regular fa-pen-to-square"></i>
          </ButtonAction>
        </Can>
        <Can do={USER_PERMISSIONS.DELETE_COURSE}>
          <ButtonAction
            onClick={() => showDialogOnDelete(data.uuid)}
            type="button"
            title={t("forms.course.buttons.delete")}
          >
            <Icon className="fa-solid fa-trash-can"></Icon>
          </ButtonAction>
        </Can>
      </div>
    );
  };

  const showDialogOnDelete = (uuid) => {
    const okAction = async () => {
      CourseService.deleteCourse(uuid).then((result) => {
        if (result && result === "OK") {
          setForceRefresh(true);
        }
      });
    };

    const cancelAction = () => {};

    ShowAlertDialog(
      t("forms.form.dialog.remove.title"),
      t("forms.form.dialog.remove.text"),
      "warning",
      true,
      t("forms.form.dialog.remove.buttonOk"),
      false,
      okAction,
      t("forms.form.dialog.remove.buttonNo"),
      cancelAction
    );
  };

  const listData = async (filter) => {
    if (sortBy) {
      filter.sort = sortBy;
    } else {
      filter.sort = "-date";
    }
    const result = await CourseService.listFilter(filter);
    console.log(result);
    return result;
  };

  const hideColumn = {};
  let resize = null;

  if (deviceSize < SIZES.md) {
    // hideColumn.itemCode = false;
    resize = false;
  } else if (deviceSize === SIZES.md) {
    // hideColumn.itemCode = false;
  }

  return (
    <>
      <PaginateList
        refresh={forceRefresh}
        filters={filters}
        columns={columns}
        toList={listData}
        hideColum={hideColumn}
        resize={resize}
        resetRefresh={() => setForceRefresh(false)}
      ></PaginateList>

      <PdfViewer
        show={showPdfViewer}
        onHide={handleClosePdfViewer}
        pdfUrl={currentPdfUrl}
        title={currentPdfTitle}
      />
    </>
  );
};

CourseListContainer.propTypes = {
  filters: PropTypes.object,
};

export default CourseListContainer;
